import type { ReactElement, ReactNode } from "react";
import React, { useId } from "react";
import MuiModal from "@mui/material/Modal";
import Close from "@/assets/svgs/close.svg";
import classNames from "classnames";
import type { StyledComponentBase } from "@/components/component-base.types";
import styles from "./modal.module.scss";
import { Button } from "../../inputs/button/button";

export interface ModalProps extends StyledComponentBase {
  /** When set the modal will include a close modal ('X') icon in the top right of the modal */
  isDismissible?: boolean;
  /** Controls the visible close icon independently from backdrop and Escape dismissal. */
  showDismissIcon?: boolean;
  /**
   * Controls the visibility of the Modal. Modals interrupt the flow and display above
   * other content.
   */
  open: boolean;
  /** Callback function called with false (`onOpenChange(false)`) when the close icon is clicked */
  onOpenChange: (arg: boolean) => void;
  /** ID of the element that labels the modal dialog */
  ariaLabelledBy?: string;
  /** Optional content displayed in the center of the modal */
  children?: string | ReactElement | ReactNode;
  /** Optional title displays at the top of the modal */
  title?: string;
  /** Function called when the primary button is clicked */
  primaryButtonAction?: () => void;
  /** The text displayed in the primary button */
  primaryButtonText?: string;
  /** Whether the primary button is disabled */
  primaryButtonDisabled?: boolean;
  /** Whether the primary button displays its loading state */
  primaryButtonLoading?: boolean;
  /** Additional class name for the primary button */
  primaryButtonClassName?: string;
  /** Optional function called when the secondary button is clicked */
  secondaryButtonAction?: () => void;
  /** Optional text displayed in the secondary button */
  secondaryButtonText?: string;
  /** Whether the secondary button is disabled */
  secondaryButtonDisabled?: boolean;
  /** Optional function called when the tertiary button is clicked */
  tertiaryButtonAction?: () => void;
  /** Optional text displayed in the tertiary button */
  tertiaryButtonText?: string;
  /** Optional container element for the modal to use as a parent */
  container?: Element | (() => Element | null) | null | undefined;
  /** Optional disables the portal and autofocus to allow an inline menu */
  disablePortalAndFocus?: boolean;
  /** If true, changes maxWidth: 50% to 100% so modal can retain a fixed width */
  isFixedWidth?: boolean;
  /** If this is set, this will use a `<form>` HTML type for the container and will pass in these props. Otherwise, if not set, this will use a `<div>`. */
  formProps?: React.FormHTMLAttributes<HTMLFormElement>;
  /** Additional class name for the modal container */
  containerClassName?: string;
  /** Additional class name for the modal button container */
  buttonContainerClassName?: string;
}

/**
 * A component that can be triggered to display a modal above the rest of the site content.
 * @param iProps Component Props
 * @returns JSX representing the Modal
 */
export const Modal = (iProps: ModalProps) => {
  const {
    isDismissible,
    showDismissIcon = isDismissible,
    open,
    onOpenChange,
    ariaLabelledBy,
    children,
    title,
    primaryButtonAction,
    primaryButtonText = "Close",
    primaryButtonDisabled,
    primaryButtonLoading,
    primaryButtonClassName,
    secondaryButtonAction,
    secondaryButtonText,
    secondaryButtonDisabled,
    tertiaryButtonAction,
    tertiaryButtonText,
    container,
    className,
    containerClassName,
    disablePortalAndFocus,
    isFixedWidth,
    formProps,
    buttonContainerClassName,
  } = iProps;

  const hasThreeButtons =
    primaryButtonText && secondaryButtonText && tertiaryButtonText;
  const generatedTitleId = useId();
  const dialogLabelledBy =
    ariaLabelledBy ?? (title ? generatedTitleId : undefined);

  const handleClose = (_event: object, reason: string) => {
    if (
      isDismissible ||
      (reason !== "backdropClick" && reason !== "escapeKeyDown")
    ) {
      onOpenChange(false);
    }
  };

  const closeIcon = (
    <span
      className={classNames(styles.closeIconContainer, {
        [styles.closeIconSolo]: !title && typeof children !== "string",
      })}
      role="button"
      onClick={() => onOpenChange(false)}
      tabIndex={0}
      onKeyDown={() => onOpenChange(false)}
      aria-label="Close"
    >
      <Close className={classNames(styles.closeIcon, "tb-gray")} />
    </span>
  );

  const Container = formProps ? "form" : "div";

  return (
    <MuiModal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={!isDismissible}
      disablePortal={disablePortalAndFocus}
      disableEnforceFocus={disablePortalAndFocus}
      disableAutoFocus={disablePortalAndFocus}
      container={container}
      className={className}
    >
      {/* @ts-expect-error The error here is caused by ts thinking the formProps are being applied to the <div>, but it's not because it must be of type <form> if formProps is set */}
      <Container
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogLabelledBy}
        className={classNames(
          styles.modalContainer,
          "background-tb-white",
          { [styles.extraPadding]: title || typeof children === "string" },
          { [styles.isFixedWidth]: !!isFixedWidth },
          containerClassName,
        )}
        {...formProps}
      >
        {showDismissIcon && <div className="u-text-right">{closeIcon}</div>}

        {title && (
          <h4
            id={generatedTitleId}
            className="mx-16 my-8 u-text-center u-subtitle-lg"
          >
            {title}
          </h4>
        )}

        <div className={classNames(styles.contentContainer, styles.scrollable)}>
          {typeof children === "string" ? (
            <p className="mx-16 my-8 u-full-width u-text-center">{children}</p>
          ) : (
            children
          )}
        </div>

        <div className={styles.buttonGroup}>
          {tertiaryButtonText ? (
            <Button
              className={styles.tertiaryButton}
              onClick={tertiaryButtonAction}
              text={tertiaryButtonText}
              variant="link"
            />
          ) : null}
          <div
            className={classNames(
              styles.containedButtonGroup,
              buttonContainerClassName,
              { [styles.threeButtons]: hasThreeButtons },
            )}
          >
            {secondaryButtonText && (
              <Button
                className={classNames(
                  styles.modalSecondaryButton,
                  styles.containedButton,
                )}
                rounded
                onClick={secondaryButtonAction}
                text={secondaryButtonText}
                variant="text"
                disabled={secondaryButtonDisabled}
              />
            )}
            {primaryButtonText && (
              <Button
                type={formProps ? "submit" : "button"}
                className={classNames(
                  styles.containedButton,
                  primaryButtonClassName,
                )}
                rounded
                onClick={primaryButtonAction}
                text={primaryButtonText ?? ""}
                disabled={primaryButtonDisabled}
                loading={primaryButtonLoading}
              />
            )}
          </div>
        </div>
      </Container>
    </MuiModal>
  );
};
